import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import { toast } from 'react-toastify';

import {
    FiBriefcase, FiMapPin, FiClock, FiCheck, FiAward,
    FiStar, FiUser, FiArrowRight, FiAlertTriangle,
    FiList, FiCode, FiArrowLeft, FiUsers, FiHeart, FiInfo, FiCalendar
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";

const API_BASE = API_BASE_URL;

function JobDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isSaved, setIsSaved] = useState(false);
    const [isApplied, setIsApplied] = useState(false);
    const [showCompanyDetails, setShowCompanyDetails] = useState(false);

    const context = useOutletContext() || {};
    const { savedJobs = [], setSavedJobs = () => {}, toggleSaveJob } = context;

    const isExpired = job?.expiryDate && new Date(job.expiryDate) < new Date(new Date().setHours(0, 0, 0, 0));

    // ✅ Function to fetch saved jobs
    const fetchSavedJobs = async () => {
        try {
            const token = localStorage.getItem("candidateToken");
            if (!token) {
                console.log('[DEBUG] No token, skipping saved jobs fetch');
                return;
            }

            console.log('[DEBUG] Fetching saved jobs...');
            const response = await axios.get(`${API_BASE_URL}/candidate/saved-jobs`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const savedJobsData = response.data.savedJobs || [];
                console.log('[DEBUG] Saved jobs fetched:', savedJobsData.length);

                // ✅ Check if current job is saved
                const currentJobId = id;
                const isJobSaved = savedJobsData.some(job => {
                    const jobId = job._id?.toString() || job.toString();
                    return jobId === currentJobId;
                });

                setIsSaved(isJobSaved);
                console.log('[DEBUG] Current job saved status:', isJobSaved);

                // Update context if available
                if (setSavedJobs) {
                    setSavedJobs(savedJobsData);
                }
            }
        } catch (error) {
            console.error('[ERROR] Fetching saved jobs:', error);
            // If unauthorized, clear token
            if (error.response?.status === 401) {
                localStorage.removeItem("candidateToken");
            }
        }
    };

    useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                setLoading(true);

                const token = localStorage.getItem("candidateToken");
                const headers = token ? { Authorization: `Bearer ${token}` } : {};

                console.log('[DEBUG] Fetching job details for ID:', id);

                const response = await axios.get(`${API_BASE_URL}/jobs/view/${id}`, { headers });

                if (response.data.success) {
                    const jobData = response.data.jobPost;
                    console.log('[DEBUG] Job data received:', jobData.role);

                    setJob(jobData);

                    // ✅ Set applied status from backend
                    setIsApplied(jobData.isApplied || false);

                    // ✅ Fetch saved jobs separately to get accurate status
                    if (token) {
                        await fetchSavedJobs();
                    } else {
                        setIsSaved(false);
                    }
                } else {
                    setError("Job post not found.");
                }
            } catch (err) {
                console.error("[ERROR] Fetching job details:", err);
                setError(err.response?.data?.message || "Failed to load job details.");
            } finally {
                setLoading(false);
            }
        };

        fetchJobDetails();
    }, [id]);

    // ✅ Handle save/unsave
    const handleToggleSave = async () => {
        const previousSavedState = isSaved;
        try {
            const token = localStorage.getItem("candidateToken");
            if (!token) {
                navigate("/candidate-login");
                return;
            }

            // Optimistic update
            setIsSaved(!previousSavedState);

            console.log('[DEBUG] Toggling save for job:', id);

            const response = await axios.post(
                `${API_BASE_URL}/candidate/saved-jobs/toggle`,
                { jobId: id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                // ✅ Update state based on response
                const newSavedStatus = response.data.isSaved !== undefined ? response.data.isSaved : !previousSavedState;
                setIsSaved(newSavedStatus);
                console.log('[DEBUG] Save toggled successfully:', newSavedStatus);

                if (newSavedStatus) {
                    toast.success("Job saved successfully!");
                } else {
                    toast.info("Job removed from saved list.");
                }

                // ✅ Refresh saved jobs list
                await fetchSavedJobs();
            } else {
                // Revert on error
                setIsSaved(previousSavedState);
                alert(response.data.message || 'Failed to save job. Please try again.');
            }
        } catch (error) {
            console.error("[ERROR] Toggling save:", error);
            // Revert on error
            setIsSaved(previousSavedState);

            if (error.response?.status === 401) {
                navigate("/candidate-login");
            } else {
                alert(error.response?.data?.message || 'Failed to save job. Please try again.');
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg,#0f172a 0%,#1e1b4b 50%,#0f172a 100%)" }}>
                <div className="flex flex-col items-center gap-6">
                    <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-2xl border-4 border-violet-900 animate-pulse" />
                        <div className="absolute inset-0 rounded-2xl border-4 border-t-violet-400 border-transparent animate-spin" />
                    </div>
                    <p style={{ color: "#a78bfa", fontSize: 11, fontWeight: 800, letterSpacing: "0.35em", textTransform: "uppercase" }}>
                        Loading Job Details...
                    </p>
                </div>
            </div>
        );
    }

    if (error || !job) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#f8fafc" }}>
                <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-12 text-center border border-red-50">
                    <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <FiAlertTriangle color="#ef4444" size={32} />
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 mb-3">Job Not Found</h2>
                    <p className="text-gray-500 text-sm mb-8">{error || "This post may have been archived."}</p>
                    <button onClick={() => navigate(-1)}
                        style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff" }}
                        className="px-8 py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-all">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const hasAssessment = (job.assessmentIds && job.assessmentIds.length > 0) || job.assessmentId;
    const postedDate = job.createdAt && !isNaN(new Date(job.createdAt).getTime())
        ? new Date(job.createdAt).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })
        : "Recently";

    const skills = job.skills
        ? job.skills.split(",").map(s => s.trim()).filter(Boolean)
        : [];

    return (
        <div className="font-sans text-slate-800 bg-gray-50" style={{ minHeight: "100vh" }}>

            {/* ── HERO ─────────────────────────────────────────────── */}
            <div className="bg-white border-b border-gray-200 shadow-sm relative overflow-hidden pt-16 pb-12">
                <div className="max-w-6xl mx-auto px-4 relative z-10">

                    {/* back link */}


                    {/* badges */}
                    <div className="flex flex-wrap gap-2 mb-5">
                        <span className="bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-lg px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider">
                            Career Opportunity
                        </span>
                        <span className="bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-lg px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-2">
                            <FiCheck size={12} />
                            Now Hiring
                        </span>
                        {hasAssessment && (
                            <span className="bg-amber-50 text-amber-600 border border-amber-200 rounded-lg px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-2">
                                <FiStar size={12} />
                                Assessment Included
                            </span>
                        )}
                        {job.expiryDate && (
                            <span className={`${isExpired ? 'bg-red-50 text-red-600 border-red-200' : 'bg-emerald-50 text-emerald-600 border-emerald-200'} border rounded-lg px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-2`}>
                                <FiCalendar size={12} />
                                {isExpired ? "Job Expired" : `Deadline: ${new Date(job.expiryDate).toLocaleDateString()}`}
                            </span>
                        )}
                        {/* Show Applied Badge */}
                        {isApplied && (
                            <span className="bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-lg px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-2">
                                <FiCheck size={12} />
                                Applied
                            </span>
                        )}
                        {/* Show Saved Badge */}
                        {isSaved && (
                            <span className="bg-rose-50 text-rose-600 border border-rose-200 rounded-lg px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-2">
                                <FiHeart size={12} fill="#e11d48" />
                                Saved
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                        <h1 className="text-gray-900 font-black tracking-tight leading-tight max-w-2xl" style={{ fontSize: "clamp(24px,4vw,40px)" }}>
                            {job.role}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4">
                            {!isApplied && !isExpired && (
                                <button
                                    onClick={() => navigate(`/applying-job/${id}`)}
                                    className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white border-none rounded-xl px-7 py-3 text-sm font-black cursor-pointer flex items-center gap-2.5 shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                                >
                                    Apply Now
                                    <FiArrowRight size={16} />
                                </button>
                            )}

                            {isExpired && !isApplied && (
                                <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-3 flex items-center gap-2">
                                    <FiAlertTriangle size={18} className="text-red-500" />
                                    <span className="text-red-600 text-sm font-black">This job has expired</span>
                                </div>
                            )}

                            {isApplied && (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-2.5 flex items-center gap-2">
                                    <FiCheck size={16} className="text-emerald-500" />
                                    <span className="text-emerald-600 text-sm font-bold">Application Submitted</span>
                                </div>
                            )}

                            {/* ✅ Save button with local state */}
                            <button
                                onClick={handleToggleSave}
                                className={`${isSaved ? 'bg-red-50 text-red-600 border-red-200' : 'bg-white text-gray-600 border-gray-200'} border rounded-xl px-5 py-3 text-sm font-black cursor-pointer flex items-center gap-2 transition-all hover:bg-gray-50`}
                            >
                                <FiHeart size={18} fill={isSaved ? "#ef4444" : "none"} />
                                {isSaved ? "Saved" : "Save"}
                            </button>

                            <button
                                onClick={() => setShowCompanyDetails(!showCompanyDetails)}
                                className={`${showCompanyDetails ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-white text-gray-600 border-gray-200'} border rounded-xl px-6 py-3 text-sm font-black cursor-pointer flex items-center gap-2 transition-all hover:bg-gray-50`}
                            >
                                <FiInfo size={18} className={showCompanyDetails ? "text-indigo-600" : ""} />
                                About Company
                            </button>

                            {localStorage.getItem("candidateToken") ? (
                                <button
                                    onClick={() => navigate("/candidate-dashboard")}
                                    className="bg-white text-gray-600 border border-gray-200 rounded-xl px-7 py-3 text-sm font-bold cursor-pointer flex items-center gap-2 transition-all hover:bg-gray-50"
                                >
                                    <FiStar size={16} className="text-indigo-500" />
                                    Dashboard
                                </button>
                            ) : (
                                <button
                                    onClick={() => navigate("/candidate-login")}
                                    className="bg-white text-gray-600 border border-gray-200 rounded-xl px-7 py-3 text-sm font-bold cursor-pointer flex items-center gap-2 transition-all hover:bg-gray-50"
                                >
                                    <FiUser size={16} className="text-indigo-500" />
                                    Login
                                </button>
                            )}
                        </div>
                    </div>

                    {/* meta pills */}
                    <div className="flex flex-wrap gap-3">
                        {job.location && (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 flex items-center gap-2">
                                <FiMapPin size={15} className="text-indigo-500" />
                                <span className="text-gray-700 text-sm font-bold">{job.location}</span>
                            </div>
                        )}
                        {job.salary && (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 flex items-center gap-2">
                                <FaRupeeSign size={15} className="text-emerald-500" />
                                <span className="text-gray-700 text-sm font-bold">{job.salary}</span>
                            </div>
                        )}
                        {job.experience && (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 flex items-center gap-2">
                                <FiAward size={15} className="text-blue-500" />
                                <span className="text-gray-700 text-sm font-bold">{job.experience} Experience</span>
                            </div>
                        )}
                        {job.department && (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 flex items-center gap-2">
                                <FiBriefcase size={15} className="text-indigo-500" />
                                <span className="text-gray-700 text-sm font-bold">{job.department}</span>
                            </div>
                        )}
                        {job.vacancies > 0 && (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 flex items-center gap-2">
                                <FiUsers size={15} className="text-amber-500" />
                                <span className="text-gray-700 text-sm font-bold">{job.vacancies} {job.vacancies === 1 ? 'Vacancy' : 'Vacancies'}</span>
                            </div>
                        )}
                        {job.jobType === "short" && job.startTime && job.endTime && (
                            <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-2 flex items-center gap-2">
                                <FiClock size={15} className="text-violet-500" />
                                <span className="text-gray-700 text-sm font-bold">{job.startTime} - {job.endTime} ({job.totalDuration})</span>
                            </div>
                        )}
                        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 flex items-center gap-2">
                            <FiClock size={15} className="text-pink-400" />
                            <span className="text-gray-500 text-sm font-bold">Posted {postedDate}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── MAIN CONTENT ─────────────────────────────────────── */}
            <div className="max-w-6xl mx-auto px-4 mt-8 relative z-10 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">

                    {/* ── Left Column ── */}
                    <div className="lg:col-span-3 space-y-6">

                        {/* About Company Card */}
                        {showCompanyDetails && (
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="p-5 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
                                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <FiBriefcase size={18} className="text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-black text-gray-900 m-0">{job.companyName || "Timely Health"}</h2>
                                        <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider m-0">Company Overview</p>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="text-gray-600 leading-relaxed text-sm font-medium">
                                        {job.aboutCompany || "Timely Health is a leading healthcare recruitment and staffing organization dedicated to connecting talented professionals with top healthcare institutions."}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Description Card */}
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                            <div className="p-5 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
                                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <FiList size={18} className="text-white" />
                                </div>
                                <div>
                                    <h2 className="text-base font-black text-gray-900 m-0">Job Description</h2>
                                    <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider m-0">What you'll be doing</p>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="text-gray-600 leading-relaxed text-sm font-medium whitespace-pre-wrap border-l-3 border-indigo-100 pl-5">
                                    {job.description || job.responsibilities || "No description provided."}
                                </div>
                            </div>
                        </div>

                        {/* Skills Card */}
                        {skills.length > 0 && (
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="p-5 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
                                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <FiCode size={18} className="text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-black text-gray-900 m-0">Required Skills</h2>
                                        <p className="text-[10px] text-fuchsia-600 font-bold uppercase tracking-wider m-0">Key qualifications we're looking for</p>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="flex flex-wrap gap-3">
                                        {skills.map((skill, idx) => (
                                            <span key={idx} className="bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-lg px-4 py-2 text-xs font-bold tracking-wide cursor-default transition-all hover:bg-indigo-600 hover:text-white hover:border-transparent">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Shift Timings Card */}
                        {job.jobType === "short" && (
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="p-5 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
                                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <FiClock size={18} className="text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-black text-gray-900 m-0">Job Timings</h2>
                                        <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider m-0">Shift Schedule</p>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Start Date</p>
                                            <p className="text-lg font-black text-gray-900">
                                                {job.startDate ? new Date(job.startDate).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">End Date</p>
                                            <p className="text-lg font-black text-gray-900">
                                                {job.endDate ? new Date(job.endDate).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Start Time</p>
                                            <p className="text-lg font-black text-gray-900">{job.startTime}</p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">End Time</p>
                                            <p className="text-lg font-black text-gray-900">{job.endTime}</p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-100">
                                            <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-1">Total Duration</p>
                                            <p className="text-lg font-black text-indigo-700">{job.totalDuration}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Location + Experience Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {job.location && (
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7">
                                    <div className="w-11 h-11 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center mb-4">
                                        <FiMapPin size={20} className="text-white" />
                                    </div>
                                    <h3 className="text-sm font-black text-gray-900 mb-2">Work Location</h3>
                                    <p className="text-sm font-bold text-gray-600">{job.location}</p>
                                </div>
                            )}
                            {job.experience && (
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7">
                                    <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
                                        <FiAward size={20} className="text-white" />
                                    </div>
                                    <h3 className="text-sm font-black text-gray-900 mb-2">Experience Required</h3>
                                    <p className="text-sm font-bold text-gray-600">{job.experience}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Right Column ── */}
                    <div className="lg:col-span-1">
                        <div style={{ position: "sticky", top: 24, display: "flex", flexDirection: "column", gap: 16 }}>

                            {/* Apply Card */}
                            <div style={{ background: "#fff", borderRadius: 24, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 8px 40px rgba(99,102,241,0.12)" }}>
                                <div style={{ height: 6, background: "linear-gradient(90deg,#6366f1,#8b5cf6,#d946ef)" }} />
                                <div style={{ padding: 28, textAlign: "center" }}>
                                    <div style={{ width: 72, height: 72, background: "linear-gradient(135deg,#eef2ff,#f3e8ff)", borderRadius: 20, border: "1px solid #e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                        <FiBriefcase size={32} color="#6366f1" />
                                    </div>

                                    <h3 style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>Join Our Team</h3>
                                    <p style={{ fontSize: 12, color: "#64748b", fontWeight: 500, lineHeight: 1.7, marginBottom: 20 }}>
                                        {hasAssessment
                                            ? "Apply and complete a short assessment to start your journey."
                                            : "Submit your application and our team will be in touch soon."}
                                    </p>

                                    {hasAssessment && (
                                        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "8px 14px", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, marginBottom: 20 }}>
                                            <FiStar size={13} color="#f59e0b" />
                                            <span style={{ fontSize: 10, fontWeight: 800, color: "#92400e", letterSpacing: "0.1em", textTransform: "uppercase" }}>Assessment Enclosed</span>
                                        </div>
                                    )}

                                    {isApplied ? (
                                        <div style={{ background: "#f1f5f9", borderRadius: 14, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                                            <FiCheck size={16} color="#22c55e" />
                                            <span style={{ fontSize: 14, fontWeight: 800, color: "#22c55e" }}>Already Applied</span>
                                        </div>
                                    ) : isExpired ? (
                                        <div style={{ background: "#fef2f2", borderRadius: 14, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                                            <FiAlertTriangle size={16} color="#ef4444" />
                                            <span style={{ fontSize: 14, fontWeight: 800, color: "#ef4444" }}>Job Expired</span>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => navigate(`/applying-job/${id}`)}
                                            style={{
                                                width: "100%",
                                                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: 14,
                                                padding: "15px 24px",
                                                fontSize: 14,
                                                fontWeight: 900,
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 10,
                                                transition: "all 0.25s",
                                                boxShadow: "0 4px 20px rgba(99,102,241,0.35)",
                                                letterSpacing: "0.02em",
                                            }}
                                            onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(99,102,241,0.45)"; }}
                                            onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(99,102,241,0.35)"; }}>
                                            Apply Now
                                            <FiArrowRight size={16} color="#fff" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Stats Card */}
                            <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", padding: "20px 24px", display: "flex", justifyContent: "space-around", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: "#6366f1" }}>24h</div>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase" }}>Response</div>
                                </div>
                                <div style={{ width: 1, background: "#f1f5f9" }} />
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: "#8b5cf6" }}>Fast</div>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase" }}>Review</div>
                                </div>
                                <div style={{ width: 1, background: "#f1f5f9" }} />
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: "#d946ef" }}>100%</div>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase" }}>Free</div>
                                </div>
                            </div>

                            {/* Company note */}
                            <div style={{ textAlign: "center", padding: "8px 0" }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.2em", textTransform: "uppercase" }}>
                                    Timely Health Recruitment
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default JobDetails;